// Component for Creating a Connection and generating QR Codes
import React, {
    useEffect,
    useRef,
    useState
} from "react";

import { Html5Qrcode } from "html5-qrcode";
import QRCode from "qrcode";
import PeerConnectionManager from "../../Services/PeerConnectionManager";
import Text from "../../Components/Text";
import Button from "../../Components/Buttons/Button";
import ScrollWrapper from "../../Components/ScrollWrapper";
import { useNavigate } from "react-router-dom";
import { getUser } from "../../Helper/workspaceStorage";

// Reduziert die SDP auf das Nötigste für lokale Netzwerke:
// nur IPv4 + UDP + typ host Kandidaten behalten (kein STUN/TURN nötig, da LAN-only)
function minifySdp(sdp) {
    return sdp
        .split("\r\n")
        .filter((line) => {
            if (!line.startsWith("a=candidate")) return true;

            const isHost = line.includes(" typ host ");
            const isUdp = line.toLowerCase().includes(" udp ");
            const ipv4Match = line.match(/candidate:\S+ \d+ \S+ \d+ (\S+) /);
            const isIPv4 = ipv4Match && ipv4Match[1].split(".").length === 4;

            return isHost && isUdp && isIPv4;
        })
        .join("\r\n");
}

async function buildQrForDescription(description) {
    const text = JSON.stringify({
        type: description.type,
        sdp: minifySdp(description.sdp)
    });
    const image = await QRCode.toDataURL(text, { errorCorrectionLevel: "L" });
    return { text, image };
}

export default function PeerConnection({ redirect = false, canUseHost = true }) {
    const [mode, setMode] = useState(null); // "host" | "client" | null
    const [step, setStep] = useState(0);
    const navigate = useNavigate();

    const [qrImage, setQrImage] = useState(null);
    const [qrText, setQrText] = useState("");
    const [manualText, setManualText] = useState("");
    const [copied, setCopied] = useState(false);
    const [scanning, setScanning] = useState(false);
    const redirectIntervalRef = useRef(null);

    const [status, setStatus] = useState(
        PeerConnectionManager.getStatus()
    );

    const peerRef = useRef(null);
    const scannerRef = useRef(null);

    function createPeer() {
        const pc = new RTCPeerConnection({
            iceServers: [
                /* { urls: "stun:stun.l.google.com:19302" } */
            ]
        });

        pc.onconnectionstatechange = () => {
            setStatus(pc.connectionState);
        };

        peerRef.current = pc;
        return pc;
    }

    async function copyToClipboard(text) {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (err) {
            console.error("Clipboard-Fehler:", err);
        }
    }

    // ---------- HOST FLOW ----------

    async function startHost() {
        setMode("host");
        setStep(0);

        const pc = createPeer();
        const channel = pc.createDataChannel("sync");

        channel.onopen = () => {
            const clientId = crypto.randomUUID();
            PeerConnectionManager.addClient(clientId, pc, channel);
            setStatus("Client verbunden");
            setStep(2);
        };

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await waitForIce(pc);

        const { text, image } = await buildQrForDescription(pc.localDescription);
        setQrText(text);
        setQrImage(image);
        setStatus("Warte auf Client");
    }

    function hostGoToScanAnswer() {
        setStep(1);
        setScanning(true);
    }

    async function processAnswer(text) {
        await peerRef.current.setRemoteDescription(JSON.parse(text));
        setManualText("");
        // step 2 wird über channel.onopen gesetzt, sobald DataChannel offen ist
    }

    // ---------- CLIENT FLOW ----------

    async function startClient() {
        setMode("client");
        setStep(0);
        createPeer();
        setStatus("Host-Code scannen oder einfügen");
        setScanning(true);
    }

    function waitForUserAndRedirect() {
        if (redirectIntervalRef.current) return;

        redirectIntervalRef.current = setInterval(() => {
            if (getUser()) {
                clearInterval(redirectIntervalRef.current);
                redirectIntervalRef.current = null;
                navigate("/app/");
            }
        }, 300);
    }

    async function processOffer(text) {
        const pc = peerRef.current;
        await pc.setRemoteDescription(JSON.parse(text));

        pc.ondatachannel = (event) => {
            const channel = event.channel;
            channel.onopen = () => {
                PeerConnectionManager.setHostConnection(pc, channel);
                setStatus("Verbunden");
                setStep(2);

                if (redirect) {
                    waitForUserAndRedirect()
                }
            };
        };

        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await waitForIce(pc);

        const { text: answerText, image } = await buildQrForDescription(pc.localDescription);
        setQrText(answerText);
        setQrImage(image);
        setManualText("");
        setStep(1);
        setStatus("Warte auf Host");
    }

    // ---------- SHARED ----------

    function waitForIce(pc) {
        return new Promise((resolve) => {
            if (pc.iceGatheringState === "complete") {
                resolve();
                return;
            }
            pc.onicecandidate = (event) => {
                if (!event.candidate) resolve();
            };
        });
    }

    async function manualConnect() {
        if (!manualText) return;

        if (mode === "client" && step === 0) {
            stopScanner();
            await processOffer(manualText);
        }

        if (mode === "host" && step === 1) {
            stopScanner();
            await processAnswer(manualText);
        }
    }

    // Scanner erst starten, wenn #reader sicher im DOM ist
    useEffect(() => {
        if (!scanning) return;

        const scanner = new Html5Qrcode("reader");
        scannerRef.current = scanner;

        scanner
            .start(
                { facingMode: "environment" },
                { fps: 10, qrbox: 250 },
                async (text) => {
                    stopScanner();
                    if (mode === "client") {
                        await processOffer(text);
                    } else if (mode === "host") {
                        await processAnswer(text);
                    }
                },
                () => { } // per-frame "kein QR erkannt", ignorieren
            )
            .catch((err) => {
                console.error("QR-Scanner Start fehlgeschlagen:", err);
            });

        return () => {
            stopScanner();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scanning]);

    function stopScanner() {
        setScanning(false);
        if (scannerRef.current) {
            scannerRef.current.stop().catch(() => { });
            scannerRef.current = null;
        }
    }

    function reset() {
        stopScanner();
        setMode(null);
        setStep(0);
        setQrImage(null);
        setQrText("");
        setManualText("");
        setStatus(PeerConnectionManager.getStatus());
        peerRef.current = null;
    }

    useEffect(() => {
        const unsubscribe = PeerConnectionManager.subscribe((s) => {
            setStatus(s);
        });

        return () => {
            unsubscribe();
            stopScanner();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="w-full h-full flex">
            <ScrollWrapper>
                <div className="w-full h-full flex flex-col items-center gap-3 p-4">
                    <Text type="main">Lokale Verbindung</Text>
                    <Text>Status: {status}</Text>

                    {!mode && (
                        <div className="flex flex-col gap-3">
                            <Text type="orderitem">Welche Rolle übernimmt dieses Gerät?</Text>
                            {canUseHost && (
                                <div>
                                <Text type="itemmain">Hauptgerät:</Text>
                                <Text type="body">Dieses Gerät soll der Host sein, welcher seine Lokale Daten wie</Text>
                                <Text type="body">Produkte und Bestellungen mit den anderen teilt</Text>
                            </div>
                            )}
                            

                            <div>
                                <Text type="itemmain">Zusatzgerät:</Text>
                                <Text type="body">Bekommt Daten vom Hauptgerät und Speichert nichts auf sich selber</Text>
                            </div>

                            {canUseHost && (
                                <Button onClickAction={startHost} text="Hauptgerät" buttonColor="bg-blue-600" />
                            )}
                            
                            <Button onClickAction={startClient} text="Zusatzgerät" buttonColor="bg-blue-600" />

                            <div>
                                <Text type="body">Beachte, dass diese Geräte im selben Netzwerk sein müssen,</Text>
                                <Text type="body">aber das Netzwerk keine Internetverbindung braucht</Text>
                            </div>
                        </div>
                    )}

                    {/* ---------- HOST ---------- */}

                    {mode === "host" && step === 0 && (
                        <div className="flex flex-col items-center gap-2 w-full max-w-sm">
                            <Text>Schritt 1: Zeige diesen Code dem zweiten Gerät</Text>
                            {qrImage && <img src={qrImage} width="300" alt="QR" />}
                            <Button
                                onClickAction={() => copyToClipboard(qrText)}
                                text={copied ? "Kopiert!" : "Code kopieren"}
                                buttonColor="bg-blue-600"
                            />
                            <Button
                                onClickAction={hostGoToScanAnswer}
                                text="Weiter: Antwort einlesen ->"
                                buttonColor="bg-green-600"
                            />
                        </div>
                    )}

                    {mode === "host" && step === 1 && (
                        <div className="flex flex-col items-center gap-2 w-full max-w-sm">
                            <Text>Schritt 2: Scanne die Antwort des zweiten Geräts</Text>
                            <div id="reader" style={{ width: "100%", maxWidth: "400px", minHeight: "300px" }} />
                            <Text>Oder Code manuell einfügen:</Text>
                            <textarea
                                value={manualText}
                                onChange={(e) => setManualText(e.target.value)}
                                className="border border-black rounded-lg w-full"
                                placeholder="Antwort-Code hier einfügen"
                            />
                            <Button onClickAction={manualConnect} text="Verbinden" buttonColor="bg-blue-600" />
                        </div>
                    )}

                    {mode === "host" && step === 2 && (
                        <div className="flex flex-col items-center gap-2">
                            <Text>Verbunden ✅</Text>
                            <Button onClickAction={reset} text="Neue Verbindung" buttonColor="bg-blue-600" />
                        </div>
                    )}

                    {/* ---------- CLIENT ---------- */}

                    {mode === "client" && step === 0 && (
                        <div className="flex flex-col items-center gap-2 w-full max-w-sm">
                            <Text>Schritt 1: Scanne den Code des Hauptgeräts</Text>
                            <div id="reader" style={{ width: "100%", maxWidth: "400px", minHeight: "300px" }} />
                            <Text>Oder Code manuell einfügen:</Text>
                            <textarea
                                value={manualText}
                                onChange={(e) => setManualText(e.target.value)}
                                className="border border-black rounded-lg w-full"
                                placeholder="Host-Code hier einfügen"
                            />
                            <Button onClickAction={manualConnect} text="Verbinden" buttonColor="bg-blue-600" />
                        </div>
                    )}

                    {mode === "client" && step === 1 && (
                        <div className="flex flex-col items-center gap-2 w-full max-w-sm">
                            <Text>Schritt 2: Zeige diesen Code dem Hauptgerät</Text>
                            {qrImage && <img src={qrImage} width="300" alt="QR" />}
                            <Button
                                onClickAction={() => copyToClipboard(qrText)}
                                text={copied ? "Kopiert!" : "Code kopieren"}
                                buttonColor="bg-blue-600"
                            />
                            <Text>Warte auf Verbindung ...</Text>
                        </div>
                    )}

                    {mode === "client" && step === 2 && (
                        <div className="flex flex-col items-center gap-2">
                            <Text>Verbunden ✅</Text>
                            <Button onClickAction={reset} text="Neue Verbindung" buttonColor="bg-blue-600" />
                        </div>
                    )}
                </div>
            </ScrollWrapper>
        </div>
    );
}